import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ECG_RIBBON_SHIFT_MHZ_DEFAULT,
  avgChoiceToTauMs,
  type RegionalAvgChoice,
  type RegionalRangeMinutes,
} from "../constants/regions";

type RibbonSettingsContextValue = {
  rangeMin: RegionalRangeMinutes;
  setRangeMin: (minutes: RegionalRangeMinutes) => void;
  avgChoice: RegionalAvgChoice;
  setAvgChoice: (choice: RegionalAvgChoice) => void;
  shiftMhz: number;
  setShiftMhz: (mhz: number) => void;
  paused: boolean;
  togglePause: () => void;
  nowMs: number;
  emaTauMs: number | null;
};

const RibbonSettingsContext = createContext<RibbonSettingsContextValue | null>(null);

export function RibbonSettingsProvider({ children }: { children: ReactNode }) {
  const [rangeMin, setRangeMin] = useState<RegionalRangeMinutes>(5);
  const [avgChoice, setAvgChoice] = useState<RegionalAvgChoice>(10);
  const [shiftMhz, setShiftMhz] = useState(ECG_RIBBON_SHIFT_MHZ_DEFAULT);
  const [paused, setPaused] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [paused]);

  const togglePause = useCallback(() => setPaused((p) => !p), []);
  const emaTauMs = avgChoiceToTauMs(avgChoice);

  const value = useMemo(
    () => ({
      rangeMin,
      setRangeMin,
      avgChoice,
      setAvgChoice,
      shiftMhz,
      setShiftMhz,
      paused,
      togglePause,
      nowMs,
      emaTauMs,
    }),
    [rangeMin, avgChoice, shiftMhz, paused, togglePause, nowMs, emaTauMs],
  );

  return (
    <RibbonSettingsContext.Provider value={value}>{children}</RibbonSettingsContext.Provider>
  );
}

export function useRibbonSettings(): RibbonSettingsContextValue {
  const ctx = useContext(RibbonSettingsContext);
  if (!ctx) {
    throw new Error("useRibbonSettings must be used within RibbonSettingsProvider");
  }
  return ctx;
}
